<template>
  <div class="homepage">
    <!-- Highlights-Leiste (eBay-Stil) -->
    <section class="highlights-bar">
      <div class="m-container">
        <div class="highlights-scroll-wrapper">
          <div class="highlights-scroll">
            <NuxtLink
              v-for="hl in highlights"
              :key="hl.title"
              :to="hl.to"
              class="highlight-card"
              :style="{ background: hl.bg }"
            >
              <MIcon :icon="hl.icon" :size="28" color="white" />
              <span class="highlight-card__title">{{ hl.title }}</span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Kategorien-Leiste (horizontal scrollbar mit Produktzahl) -->
    <section class="categories-bar">
      <div class="m-container">
        <div class="categories-scroll">
          <NuxtLink
            v-for="cat in categories"
            :key="cat.id"
            :to="getCatPath(cat)"
            class="cat-pill"
          >
            <MIcon :icon="categoryIcon(cat.slug)" :size="16" />
            {{ cat.name }}
            <span v-if="cat.children?.length" class="cat-pill__count">{{ cat.children.length }}</span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- 3. Kategorie-Karten Grid (Amazon-Stil: 4er Grid mit Subcategory-Bildern) -->
    <section class="py-6">
      <div class="m-container">
        <div class="section-header mb-4">
          <h2 class="section-title">Entdecke nach Raum</h2>
          <NuxtLink to="/kategorien" class="section-link">
            Alle anzeigen <MIcon icon="arrow-right" size="small" />
          </NuxtLink>
        </div>

        <div v-if="categoriesStore.loading" class="category-grid">
          <div v-for="n in 8" :key="n" class="category-card" style="overflow: hidden">
            <div style="aspect-ratio: 4/3"><MSkeletonLoader type="image" /></div>
            <div class="pa-3"><MSkeletonLoader type="text" width="80%" /></div>
          </div>
        </div>

        <div v-else class="category-grid">
          <NuxtLink
            v-for="(cat, i) in categories.slice(0, 8)"
            :key="cat.id"
            :to="getCatPath(cat)"
            class="category-card"
          >
            <div class="category-card__img-wrap">
              <img
                v-if="getCategoryImage(cat)"
                :src="getCategoryImage(cat)"
                :alt="cat.name"
                :loading="i < 4 ? 'eager' : 'lazy'"
                :fetchpriority="i === 0 ? 'high' : undefined"
                decoding="async"
                class="category-card__img"
              />
              <div v-else class="category-card__placeholder">
                <MIcon icon="sofa-outline" color="grey-lighten-2" :size="32" />
              </div>
              <span class="category-card__badge">{{ cat.children?.length || 0 }} Kategorien</span>
            </div>
            <div class="category-card__info">
              <h3 class="category-card__title">
                <MIcon :icon="categoryIcon(cat.slug)" :size="18" class="category-card__icon" />
                {{ cat.name }}
              </h3>
              <span class="category-card__link">
                Entdecken <MIcon icon="chevron-right" size="x-small" />
              </span>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Stark reduziert -->
    <section class="py-6 bg-section">
      <div class="m-container">
        <div class="section-header mb-4">
          <h2 class="section-title">
            <MIcon icon="tag-outline" :size="20" color="secondary" class="mr-1" />
            Stark reduziert
          </h2>
          <NuxtLink to="/angebote" class="section-link">
            Alle anzeigen <MIcon icon="arrow-right" size="small" />
          </NuxtLink>
        </div>

        <div class="product-scroll-wrapper"><div class="product-scroll">
          <template v-if="slidersLoading">
            <div v-for="n in 8" :key="n" class="product-scroll__item">
              <ProductCardSkeleton />
            </div>
          </template>
          <template v-else>
            <div v-for="product in dealProducts" :key="product.id" class="product-scroll__item">
              <ProductCard :product="product" />
            </div>
          </template>
        </div></div>
      </div>
    </section>

    <!-- 5. Weitere Kategorien (zweite Reihe) -->
    <section v-if="categories.length > 8" class="py-6">
      <div class="m-container">
        <div class="category-grid">
          <NuxtLink
            v-for="cat in categories.slice(8, 16)"
            :key="cat.id"
            :to="getCatPath(cat)"
            class="category-card"
          >
            <div class="category-card__img-wrap">
              <img
                v-if="getCategoryImage(cat)"
                :src="getCategoryImage(cat)"
                :alt="cat.name"
                loading="lazy"
                class="category-card__img"
              />
              <div v-else class="category-card__placeholder">
                <MIcon icon="sofa-outline" color="grey-lighten-2" :size="32" />
              </div>
              <span class="category-card__badge">{{ cat.children?.length || 0 }} Kategorien</span>
            </div>
            <div class="category-card__info">
              <h3 class="category-card__title">
                <MIcon :icon="categoryIcon(cat.slug)" :size="18" class="category-card__icon" />
                {{ cat.name }}
              </h3>
              <span class="category-card__link">
                Entdecken <MIcon icon="chevron-right" size="x-small" />
              </span>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Neu eingetroffen -->
    <section class="py-6 bg-section">
      <div class="m-container">
        <div class="section-header mb-4">
          <h2 class="section-title">
            <MIcon icon="new-box" :size="20" color="primary" class="mr-1" />
            Neu eingetroffen
          </h2>
          <NuxtLink to="/suche?orderBy=newest" class="section-link">
            Alle anzeigen <MIcon icon="arrow-right" size="small" />
          </NuxtLink>
        </div>
        <div class="product-scroll-wrapper"><div class="product-scroll">
          <template v-if="slidersLoading">
            <div v-for="n in 8" :key="n" class="product-scroll__item"><ProductCardSkeleton /></div>
          </template>
          <template v-else>
            <div v-for="product in newestProducts" :key="product.id" class="product-scroll__item">
              <ProductCard :product="product" />
            </div>
          </template>
        </div></div>
      </div>
    </section>

    <!-- Beliebt -->
    <section v-if="popularProducts.length" class="py-6">
      <div class="m-container">
        <div class="section-header mb-4">
          <h2 class="section-title">
            <MIcon icon="fire" :size="20" color="secondary" class="mr-1" />
            Beliebt
          </h2>
          <NuxtLink to="/suche" class="section-link">
            Alle anzeigen <MIcon icon="arrow-right" size="small" />
          </NuxtLink>
        </div>
        <div class="product-scroll-wrapper"><div class="product-scroll">
          <div v-for="product in popularProducts" :key="product.id" class="product-scroll__item">
              <ProductCard :product="product" />
          </div>
        </div></div>
      </div>
    </section>

    <!-- Preis-Leistung -->
    <section v-if="valueProducts.length" class="py-6 bg-section">
      <div class="m-container">
        <div class="section-header mb-4">
          <h2 class="section-title">
            <MIcon icon="cash-multiple" :size="20" color="primary" class="mr-1" />
            Preis-Leistungs-Hits
          </h2>
          <NuxtLink to="/angebote" class="section-link">
            Alle anzeigen <MIcon icon="arrow-right" size="small" />
          </NuxtLink>
        </div>
        <div class="product-scroll-wrapper"><div class="product-scroll">
          <div v-for="product in valueProducts" :key="product.id" class="product-scroll__item">
              <ProductCard :product="product" />
          </div>
        </div></div>
      </div>
    </section>

    <!-- Beliebt in Kategorien — dynamische Slider pro Kategorie -->
    <template v-for="catSlider in categorySliders" :key="catSlider.slug">
      <section v-if="catSlider.products.length > 0" class="py-6" :class="{ 'bg-section': catSlider.alt }">
        <div class="m-container">
          <div class="section-header mb-1">
            <h2 class="section-title">
              <MIcon :icon="categoryIcon(catSlider.slug)" :size="20" class="mr-1" />
              {{ catSlider.name }}
            </h2>
            <NuxtLink :to="'/' + catSlider.slug" class="section-link">
              Alle {{ catSlider.name }} ansehen <MIcon icon="arrow-right" size="small" />
            </NuxtLink>
          </div>
          <p v-if="catSlider.subtitle" class="text-body-2 text-medium-emphasis mb-4">{{ catSlider.subtitle }}</p>
          <div class="product-scroll-wrapper"><div class="product-scroll">
            <div v-for="product in catSlider.products" :key="product.id" class="product-scroll__item">
              <ProductCard :product="product" />
            </div>
          </div></div>
        </div>
      </section>
    </template>

    <!-- Trending Suchbegriffe -->
    <section class="py-6">
      <div class="m-container">
        <div class="section-header mb-4">
          <h2 class="section-title">Beliebte Suchen</h2>
        </div>
        <div class="trending-grid">
          <NuxtLink
            v-for="term in trendingTerms"
            :key="term.query"
            :to="`/suche?q=${encodeURIComponent(term.query)}`"
            class="trending-card"
          >
            <MIcon :icon="term.icon" :size="20" class="trending-card__icon" />
            <span>{{ term.label }}</span>
            <MIcon icon="arrow-right" :size="14" class="trending-card__arrow" />
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Magazin -->
    <section class="py-6">
      <div class="m-container">
        <div class="section-header mb-4">
          <h2 class="section-title">
            <MIcon icon="newspaper-variant-outline" :size="20" color="secondary" class="mr-1" />
            Magazin
          </h2>
          <NuxtLink to="/magazin" class="section-link">
            Alle Artikel <MIcon icon="arrow-right" size="small" />
          </NuxtLink>
        </div>
        <div class="magazin-grid">
          <NuxtLink
            v-for="article in magazinArticles"
            :key="article.slug"
            :to="`/magazin/${article.slug}`"
            class="magazin-card"
          >
            <div class="magazin-card__img-wrap">
              <img :src="article.image" :alt="article.title" loading="lazy" class="magazin-card__img" />
              <span class="magazin-card__tag">{{ article.tag }}</span>
            </div>
            <div class="magazin-card__body">
              <h3 class="magazin-card__title">{{ article.title }}</h3>
              <p class="magazin-card__excerpt">{{ article.excerpt }}</p>
              <span class="magazin-card__read">
                Weiterlesen <MIcon icon="arrow-right" size="x-small" />
              </span>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
import { useCategoriesStore, type CategoryItem } from "~/stores/categories";

// Fallback Kategorie-Bilder (statisch)
import imgBuero from "~/assets/img/catimages/buero.jpg";
import imgBad from "~/assets/img/catimages/bad.jpg";
import imgGarten from "~/assets/img/catimages/garten.jpg";
import imgBaumarkt from "~/assets/img/catimages/flurmoebel.jpg";
import imgDeko from "~/assets/img/catimages/deko.jpg";
import imgEssen from "~/assets/img/catimages/esszimmer.jpg";
import imgFlur from "~/assets/img/catimages/flur.jpg";
import imgKinder from "~/assets/img/catimages/kinderzimmer.jpg";
import imgSchlafen from "~/assets/img/catimages/schlafzimmer.jpg";
import imgLeuchten from "~/assets/img/catimages/leuchten.jpg";
import imgTextilien from "~/assets/img/catimages/dekoration.jpg";
import imgWohnen from "~/assets/img/catimages/wohnen.jpg";


useSeoMeta({
  title: "Möbel Preisvergleich - Dein Möbel-Portal",
  description:
    "Vergleiche Möbelpreise aus hunderten Online-Shops. Kostenlos, unabhängig und transparent. Finde deine Traummöbel zum besten Preis.",
  ogTitle: "Moebix.de - Möbel Preisvergleich",
  ogDescription:
    "Vergleiche Möbelpreise aus hunderten Online-Shops. Kostenlos und unabhängig.",
  ogImage: "/moebix_og_sticker.jpg",
  ogType: "website",
});

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebSite",
            name: "Moebix.de",
            url: "https://www.moebix.de",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://www.moebix.de/suche?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@type": "Organization",
            name: "Moebix.de",
            url: "https://www.moebix.de",
            logo: "https://www.moebix.de/favicon.svg",
            description:
              "Dein Möbel-Portal für Preisvergleich, Magazin und Einrichtungsideen.",
          },
        ],
      }),
    },
  ],
});

const api = useApi();
const categoriesStore = useCategoriesStore();
const searchQuery = ref("");

const getCatPath = (cat: CategoryItem) =>
  "/" + categoriesStore.getCategoryPath(cat);

const getSubCatPath = (parent: CategoryItem, sub: CategoryItem) =>
  "/" + [parent.slug, sub.slug].filter(Boolean).join("/");

onMounted(() => {
  categoriesStore.fetchCategories();
});

const categories = computed(() => categoriesStore.categories || []);

// Fallback-Bilder nach Kategorie-Name
const fallbackImages: Record<string, string> = {
  "Büro": imgBuero,
  "Bad": imgBad,
  "Garten": imgGarten,
  "Baumarkt": imgBaumarkt,
  "Deko": imgDeko,
  "Essen": imgEssen,
  "Flur": imgFlur,
  "Kinder": imgKinder,
  "Schlafen": imgSchlafen,
  "Leuchten": imgLeuchten,
  "Textilien": imgTextilien,
  "Wohnen": imgWohnen,
};

// Produktbilder aus DB (überschreibt Fallback wenn vorhanden)
const { data: categoryImagesData } = useAsyncData("category-images",
  () => api<Record<string, string>>("/api/categories/images"),
  { lazy: true },
);
const dbImages = computed(() => categoryImagesData.value || {});

// Bild für eine Kategorie: DB-Bild > Fallback-Bild
const getCategoryImage = (cat: CategoryItem): string | undefined => {
  return dbImages.value[cat.id] || fallbackImages[cat.name];
};

// Alle Homepage-Daten in einem Call (Sliders + Kategorie-Produkte)
// Non-lazy: SSR rendert mit Produkt-Daten → Bots sehen Inhalt, LCP-Image ist
// im initial-HTML enthalten. Backend-Cache (CacheTTL 600s) + Nuxt-SWR (600s)
// halten den Roundtrip kurz.
const { data: homepageData, status: homepageStatus } = await useAsyncData("homepage-all",
  () => api<any>("/api/homepage/all"),
);
const slidersLoading = computed(() => homepageStatus.value === 'pending');
const dealProducts = computed(() => homepageData.value?.deals || []);
const newestProducts = computed(() => homepageData.value?.newest || []);
const popularProducts = computed(() => homepageData.value?.popular || []);
const valueProducts = computed(() => homepageData.value?.valueDeals || []);

const popularTags = ["Sofa", "Esstisch", "Bett", "Schreibtisch", "Regal", "Lampe"];

// Highlights (eBay-Stil Kacheln)
const highlights = [
  { title: "Bis 50% Rabatt", icon: "tag-outline", to: "/angebote", bg: "linear-gradient(135deg, #e34444, #ff6b6b)" },
  { title: "Neue Sofas", icon: "sofa-outline", to: "/wohnen/sofas", bg: "linear-gradient(135deg, #3c8f93, #50c4bc)" },
  { title: "Schlafzimmer", icon: "bed-outline", to: "/schlafen", bg: "linear-gradient(135deg, #6366f1, #818cf8)" },
  { title: "Garten-Saison", icon: "flower", to: "/garten", bg: "linear-gradient(135deg, #16a34a, #4ade80)" },
  { title: "Büromöbel", icon: "desk-lamp", to: "/buero", bg: "linear-gradient(135deg, #ea580c, #fb923c)" },
  { title: "Küche", icon: "silverware-fork-knife", to: "/essen", bg: "linear-gradient(135deg, #9333ea, #c084fc)" },
  { title: "Badezimmer", icon: "shower", to: "/bad", bg: "linear-gradient(135deg, #0891b2, #22d3ee)" },
  { title: "Beleuchtung", icon: "ceiling-light", to: "/beleuchtung", bg: "linear-gradient(135deg, #d97706, #fbbf24)" },
  { title: "Regale", icon: "bookshelf", to: "/wohnen/regale", bg: "linear-gradient(135deg, #65a30d, #a3e635)" },
  { title: "Tische", icon: "table-furniture", to: "/wohnen/tische", bg: "linear-gradient(135deg, #7c3aed, #a78bfa)" },
  { title: "Stühle", icon: "seat", to: "/wohnen/stuehle", bg: "linear-gradient(135deg, #dc2626, #f87171)" },
  { title: "Dekoration", icon: "flower-tulip-outline", to: "/dekoration", bg: "linear-gradient(135deg, #db2777, #f472b6)" },
  { title: "Kinderzimmer", icon: "baby-face-outline", to: "/kinderzimmer", bg: "linear-gradient(135deg, #2563eb, #60a5fa)" },
  { title: "Aufbewahrung", icon: "archive-outline", to: "/aufbewahrung", bg: "linear-gradient(135deg, #059669, #34d399)" },
];

// Kategorie-Slider: 4 Hauptkategorien mit eigenen Produkt-Slidern
const categorySliderSlugs = ['wohnen', 'schlafen', 'buero', 'garten'];

function getCategoryBySlug(slug: string): CategoryItem | undefined {
  return categoriesStore.categories.find((c) => c.slug === slug);
}

const categorySliders = computed(() =>
  categorySliderSlugs.map((slug, i) => {
    const cat = getCategoryBySlug(slug);
    return {
      slug,
      name: cat?.name || slug,
      subtitle: cat?.titleDescription || "",
      products: homepageData.value?.categoryProducts?.[slug] || [],
      alt: i % 2 === 1,
    };
  })
);

// Trending Suchbegriffe
const trendingTerms = [
  { query: "Ecksofa", label: "Ecksofas", icon: "sofa-outline" },
  { query: "Esstisch Holz", label: "Esstische aus Holz", icon: "table-furniture" },
  { query: "Kleiderschrank", label: "Kleiderschränke", icon: "wardrobe-outline" },
  { query: "Matratze 140x200", label: "Matratzen 140x200", icon: "bed-outline" },
  { query: "Stehlampe", label: "Stehlampen", icon: "floor-lamp" },
  { query: "Bücherregal", label: "Bücherregale", icon: "bookshelf" },
  { query: "Schreibtisch", label: "Schreibtische", icon: "desk" },
  { query: "Barhocker", label: "Barhocker", icon: "seat-outline" },
  { query: "Couchtisch", label: "Couchtische", icon: "table-furniture" },
  { query: "Kommode", label: "Kommoden", icon: "dresser-outline" },
  { query: "Badezimmer Spiegel", label: "Badspiegel", icon: "mirror-rectangle" },
  { query: "Gartenmöbel Set", label: "Gartenmöbel Sets", icon: "umbrella-beach" },
];

// Magazin-Artikel (statisch, später aus DB/CMS)
const magazinArticles = [
  {
    slug: "kleine-wohnung-einrichten",
    title: "Kleine Wohnung einrichten: 10 clevere Tipps",
    excerpt: "Mit diesen Tricks wirkt auch eine kleine Wohnung geräumig und gemütlich.",
    tag: "Einrichtung",
    image: imgWohnen,
  },
  {
    slug: "schlafzimmer-trends",
    title: "Schlafzimmer-Trends: Das ist 2025 angesagt",
    excerpt: "Von Naturmaterialien bis Smart Home — die wichtigsten Trends fürs Schlafzimmer.",
    tag: "Trends",
    image: imgSchlafen,
  },
  {
    slug: "garten-lounge-einrichten",
    title: "Garten-Lounge perfekt einrichten",
    excerpt: "So wird dein Garten zum Outdoor-Wohnzimmer mit Lounge-Feeling.",
    tag: "Garten",
    image: imgGarten,
  },
];

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    navigateTo(`/suche?q=${encodeURIComponent(searchQuery.value.trim())}`);
  }
};
</script>

<style scoped>
/* ===== Categories Bar (Chinapot-Stil: horizontale Pillen) ===== */
.categories-bar {
  background: var(--m-surface);
  border-bottom: 1px solid var(--m-border-light);
  padding: 10px 0;
}
.categories-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
}
.categories-scroll::-webkit-scrollbar { display: none; }

.cat-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: var(--m-radius-full);
  font-size: var(--m-font-size-sm);
  font-weight: 500;
  color: var(--m-on-surface);
  background: var(--m-surface-variant);
  text-decoration: none;
  transition: all var(--m-transition-fast);
  flex-shrink: 0;
}
.cat-pill:hover { background: var(--m-primary); color: var(--m-on-primary); }
.cat-pill__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  border-radius: var(--m-radius);
  background: var(--m-primary);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 0 5px;
}
.cat-pill:hover .cat-pill__count { background: rgba(255,255,255,0.3); }

/* ===== Section Layout ===== */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.section-title {
  font-family: var(--m-font-body);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--m-on-surface);
}
.section-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--m-primary);
  font-size: var(--m-font-size-sm);
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}
.section-link:hover { text-decoration: underline; }
.bg-section { background: var(--m-bg); }

/* ===== Category Grid (Chinapot-Stil) ===== */
.category-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (min-width: 600px) { .category-grid { grid-template-columns: repeat(3, 1fr); gap: 12px; } }
@media (min-width: 960px) { .category-grid { grid-template-columns: repeat(4, 1fr); gap: 14px; } }

.category-card {
  display: block;
  text-decoration: none;
  color: var(--m-on-surface);
  background: var(--m-surface);
  border-radius: var(--m-radius);
  overflow: hidden;
  border: 1px solid var(--m-border-light);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.category-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.category-card__img-wrap {
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: var(--m-surface-variant);
}

.category-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.category-card:hover .category-card__img {
  transform: scale(1.05);
}

.category-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m-surface-variant);
}

.category-card__badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
}

.category-card__info {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.category-card__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--m-on-surface);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-card__icon {
  color: var(--m-primary);
  flex-shrink: 0;
}

.category-card__link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--m-primary);
  font-size: var(--m-font-size-xs);
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}
.category-card:hover .category-card__link { color: var(--m-secondary); }

/* ===== Product Scroll (Chinapot-Stil: horizontal scrollbar) ===== */
.product-scroll-wrapper {
  position: relative;
}
.product-scroll-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 4px;
  width: 48px;
  background: linear-gradient(to right, transparent, var(--m-surface));
  pointer-events: none;
  z-index: 1;
}
.product-scroll {
  display: flex;
  gap: var(--m-space-3);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}
.product-scroll::-webkit-scrollbar { display: none; }

.product-scroll__item {
  flex: 0 0 160px;
  scroll-snap-align: start;
}
@media (min-width: 600px) { .product-scroll__item { flex: 0 0 200px; } }
@media (min-width: 960px) { .product-scroll__item { flex: 0 0 220px; } }

.product-scroll__link { text-decoration: none; display: block; height: 100%; }


/* ===== Magazin Grid ===== */
.magazin-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 600px) {
  .magazin-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 960px) {
  .magazin-grid { grid-template-columns: repeat(3, 1fr); }
}

.magazin-card {
  display: block;
  text-decoration: none;
  color: var(--m-on-surface);
  background: var(--m-surface);
  border-radius: var(--m-radius);
  overflow: hidden;
  border: 1px solid var(--m-border-light);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.magazin-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.magazin-card__img-wrap {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}
.magazin-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.magazin-card:hover .magazin-card__img {
  transform: scale(1.05);
}

.magazin-card__tag {
  position: absolute;
  top: 10px;
  left: 10px;
  background: var(--m-secondary);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: var(--m-radius);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.magazin-card__body {
  padding: 16px;
}

.magazin-card__title {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.magazin-card__excerpt {
  font-size: 0.8125rem;
  color: var(--m-on-surface-variant);
  line-height: 1.5;
  margin: 0 0 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.magazin-card__read {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--m-primary);
}
.magazin-card:hover .magazin-card__read {
  text-decoration: underline;
}

/* ===== Spacing ===== */
/* ===== Highlights Bar (eBay-Stil) ===== */
.highlights-bar {
  background: var(--m-surface);
  padding: 16px 0;
}

.highlights-scroll-wrapper {
  position: relative;
}
.highlights-scroll-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  background: linear-gradient(to right, transparent, var(--m-surface));
  pointer-events: none;
}
.highlights-scroll {
  display: flex;
  gap: var(--m-space-3);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
}
.highlights-scroll::-webkit-scrollbar { display: none; }

.highlight-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 130px;
  height: 100px;
  border-radius: var(--m-radius);
  text-decoration: none;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: center;
  padding: 12px 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.highlight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.highlight-card__title {
  line-height: 1.2;
}

@media (min-width: 600px) {
  .highlight-card {
    flex: 0 0 150px;
    height: 110px;
    font-size: 0.875rem;
  }
}

/* ===== Trending Grid ===== */
.trending-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
@media (min-width: 600px) {
  .trending-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; }
}
@media (min-width: 960px) {
  .trending-grid { grid-template-columns: repeat(4, 1fr); gap: 12px; }
}

.trending-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: var(--m-surface);
  border: 1px solid var(--m-border-light);
  border-radius: var(--m-radius);
  text-decoration: none;
  color: var(--m-on-surface);
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.15s ease;
}
.trending-card:hover {
  border-color: var(--m-primary);
  background: color-mix(in srgb, var(--m-primary) 5%, var(--m-surface));
}
.trending-card__icon {
  color: var(--m-primary);
  flex-shrink: 0;
}
.trending-card__arrow {
  margin-left: auto;
  color: var(--m-on-surface-variant);
  opacity: 0;
  transition: opacity 0.15s ease;
}
.trending-card:hover .trending-card__arrow {
  opacity: 1;
}

.py-6 { padding: 24px 0; }
.py-8 { padding: 32px 0; }
</style>
